import type { User as FirebaseUser } from "firebase/auth";

import type { UserResponseDto } from "@/generated/model";
import api from "@/lib/api";

export interface SessionUser extends UserResponseDto {
  idToken: string;
  firebaseUser: FirebaseUser;
}

/**
 * Fetches the current user's data from the API using their Firebase ID token.
 * This replaces the NextAuth session fetch mechanism.
 */
export async function fetchUserSession(
  firebaseUser: FirebaseUser
): Promise<SessionUser> {
  const idToken = await firebaseUser.getIdToken();

  // Fetch user data from the API using the existing /v1/users/self endpoint
  const { data } = await api<UserResponseDto>({
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    method: "get",
    url: `/v1/users/self`,
  });

  return {
    ...data,
    idToken,
    firebaseUser,
  };
}

/**
 * Refreshes the user's session by fetching updated user data from the API.
 * Firebase SDK handles token refresh automatically.
 */
export async function refreshUserSession(
  firebaseUser: FirebaseUser
): Promise<SessionUser> {
  // Firebase SDK handles token refresh automatically via getIdToken(true)
  const idToken = await firebaseUser.getIdToken(true);

  const { data } = await api<UserResponseDto>({
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
    method: "get",
    url: `/v1/users/self`,
  });

  return {
    ...data,
    idToken,
    firebaseUser,
  };
}
